import { useId, useState } from "react"

import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Slider,
  Tooltip,
} from "@coval/ui"
import { Eye, EyeOff } from "lucide-react"

import {
  getTimeoutSecondsError,
  parseTimeoutSecondsInput,
} from "@/utils/simulation/realtimeFieldValidation"
import {
  GEMINI_REALTIME_DEFAULT_MODEL,
  GEMINI_REALTIME_DEFAULT_VOICE,
  GEMINI_REALTIME_MODELS,
  GEMINI_REALTIME_VOICES,
  GROK_REALTIME_DEFAULT_MODEL,
  GROK_REALTIME_DEFAULT_VOICE,
  GROK_REALTIME_MODELS,
  GROK_REALTIME_VOICES,
  OPENAI_GPT_LIVE_ALPHA_MODELS,
  OPENAI_GPT_LIVE_ALPHA_ONLY_VOICE_VALUES,
  OPENAI_REALTIME_DEFAULT_MODEL,
  OPENAI_REALTIME_DEFAULT_VOICE,
  OPENAI_REALTIME_MODELS,
  OPENAI_REALTIME_VOICES,
  type OpenAIRealtimeApiKeySource,
} from "@/utils/simulation/simulatorFields"

import { FieldLabel } from "./FieldLabel"
import {
  GeminiRealtimeAdvancedFields,
  GrokRealtimeAdvancedFields,
  OpenAIRealtimeAdvancedFields,
  RealtimeAdvancedSettingsCard,
} from "./RealtimeAdvancedSettingsFields"
import { RealtimeInstructionsField } from "./RealtimeInstructionsField"

interface BaseFormFieldProps {
  disabled?: boolean
  className?: string
  hideAdvancedSettings?: boolean
  hidePlaceholders?: boolean
}

interface GeminiRealtimeFieldsProps extends BaseFormFieldProps {
  apiKey: string
  instructions?: string
  model?: string
  voice?: string
  temperature?: number
  timeoutSeconds?: number
  onApiKeyChange: (value: string) => void
  onInstructionsChange: (value: string) => void
  onModelChange: (value: string) => void
  onVoiceChange?: (value: string) => void
  onTemperatureChange: (value: number | undefined) => void
  onTimeoutSecondsChange: (value: number | undefined) => void
}

export const GeminiRealtimeFields: React.FC<GeminiRealtimeFieldsProps> = ({
  apiKey,
  instructions,
  model,
  voice,
  temperature,
  timeoutSeconds,
  onApiKeyChange,
  onInstructionsChange,
  onModelChange,
  onVoiceChange,
  onTemperatureChange,
  onTimeoutSecondsChange,
  disabled,
  className = "",
  hideAdvancedSettings = false,
  hidePlaceholders = false,
}) => {
  const fieldIdBase = useId()
  const [isApiKeyVisible, setIsApiKeyVisible] = useState(false)

  return (
    <div className="space-y-4">
      <div className={onVoiceChange ? "grid grid-cols-2 gap-4" : ""}>
        <div className="space-y-2">
          <Label htmlFor={`${fieldIdBase}-model`}>Model</Label>
          <Select
            value={model || GEMINI_REALTIME_DEFAULT_MODEL}
            onValueChange={(value) => {
              if (value) {
                onModelChange(value)
              }
            }}
            disabled={disabled}
          >
            <SelectTrigger
              id={`${fieldIdBase}-model`}
              aria-label="Select Gemini Live Model"
            >
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {GEMINI_REALTIME_MODELS.map((modelOption) => (
                <SelectItem key={modelOption.value} value={modelOption.value}>
                  {modelOption.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {onVoiceChange && (
          <div className="space-y-2">
            <Label
              id={`${fieldIdBase}-voice-label`}
              htmlFor={`${fieldIdBase}-voice`}
            >
              Agent Voice
            </Label>
            <Select
              value={voice || GEMINI_REALTIME_DEFAULT_VOICE}
              onValueChange={(value) => {
                if (value) {
                  onVoiceChange(value)
                }
              }}
              disabled={disabled}
            >
              <SelectTrigger
                id={`${fieldIdBase}-voice`}
                aria-labelledby={`${fieldIdBase}-voice-label`}
              >
                <SelectValue placeholder="Select a voice" />
              </SelectTrigger>
              <SelectContent>
                {GEMINI_REALTIME_VOICES.map((voiceOption) => (
                  <SelectItem
                    key={voiceOption.value}
                    value={voiceOption.value}
                    description={voiceOption.description}
                  >
                    {voiceOption.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${fieldIdBase}-api-key`}>Google AI API Key</Label>
        <Input
          id={`${fieldIdBase}-api-key`}
          type={isApiKeyVisible ? "text" : "password"}
          autoComplete="new-password"
          data-lpignore="true"
          data-1p-ignore
          data-op-ignore
          data-bwignore="true"
          placeholder={
            hidePlaceholders
              ? "Enter your Google AI API key"
              : "AIzaSy… or AQ.Ab…"
          }
          value={apiKey}
          onChange={(e) => onApiKeyChange(e.target.value)}
          className={className}
          disabled={disabled}
          required
          endAdornment={
            <Tooltip
              content={isApiKeyVisible ? "Hide API key" : "Show API key"}
            >
              <button
                type="button"
                onClick={() => setIsApiKeyVisible(!isApiKeyVisible)}
                aria-label={isApiKeyVisible ? "Hide API key" : "Show API key"}
              >
                {isApiKeyVisible ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </Tooltip>
          }
        />
      </div>
      <RealtimeInstructionsField
        value={instructions}
        placeholder={
          hidePlaceholders
            ? "Describe how your agent should respond"
            : undefined
        }
        onChange={onInstructionsChange}
        className={className}
        disabled={disabled}
      />
      {!hideAdvancedSettings && (
        <RealtimeAdvancedSettingsCard>
          <GeminiRealtimeAdvancedFields
            temperature={temperature}
            timeoutSeconds={timeoutSeconds}
            onTemperatureChange={onTemperatureChange}
            onTimeoutSecondsChange={onTimeoutSecondsChange}
            disabled={disabled}
            className={className}
          />
        </RealtimeAdvancedSettingsCard>
      )}
    </div>
  )
}

// NOTE: no temperature prop — the Grok Voice Agent API does not expose a
// temperature knob in its session config.
interface GrokRealtimeFieldsProps extends BaseFormFieldProps {
  apiKey: string
  instructions?: string
  model?: string
  voice?: string
  timeoutSeconds?: number
  onApiKeyChange: (value: string) => void
  onInstructionsChange: (value: string) => void
  onModelChange: (value: string) => void
  onVoiceChange?: (value: string) => void
  onTimeoutSecondsChange: (value: number | undefined) => void
}

export const GrokRealtimeFields: React.FC<GrokRealtimeFieldsProps> = ({
  apiKey,
  instructions,
  model,
  voice,
  timeoutSeconds,
  onApiKeyChange,
  onInstructionsChange,
  onModelChange,
  onVoiceChange,
  onTimeoutSecondsChange,
  disabled,
  className = "",
  hideAdvancedSettings = false,
  hidePlaceholders = false,
}) => {
  const fieldIdBase = useId()
  const [isApiKeyVisible, setIsApiKeyVisible] = useState(false)

  return (
    <div className="space-y-4">
      <div className={onVoiceChange ? "grid grid-cols-2 gap-4" : ""}>
        <div className="space-y-2">
          <Label htmlFor={`${fieldIdBase}-model`}>Model</Label>
          <Select
            value={model || GROK_REALTIME_DEFAULT_MODEL}
            onValueChange={(value) => {
              if (value) {
                onModelChange(value)
              }
            }}
            disabled={disabled}
          >
            <SelectTrigger
              id={`${fieldIdBase}-model`}
              aria-label="Select Grok Realtime Model"
            >
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {GROK_REALTIME_MODELS.map((modelOption) => (
                <SelectItem key={modelOption.value} value={modelOption.value}>
                  {modelOption.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {onVoiceChange && (
          <div className="space-y-2">
            <Label
              id={`${fieldIdBase}-voice-label`}
              htmlFor={`${fieldIdBase}-voice`}
            >
              Agent Voice
            </Label>
            <Select
              value={voice || GROK_REALTIME_DEFAULT_VOICE}
              onValueChange={(value) => {
                if (value) {
                  onVoiceChange(value)
                }
              }}
              disabled={disabled}
            >
              <SelectTrigger
                id={`${fieldIdBase}-voice`}
                aria-labelledby={`${fieldIdBase}-voice-label`}
              >
                <SelectValue placeholder="Select a voice" />
              </SelectTrigger>
              <SelectContent>
                {GROK_REALTIME_VOICES.map((voiceOption) => (
                  <SelectItem
                    key={voiceOption.value}
                    value={voiceOption.value}
                    description={voiceOption.description}
                  >
                    {voiceOption.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${fieldIdBase}-api-key`}>xAI API Key</Label>
        <Input
          id={`${fieldIdBase}-api-key`}
          type={isApiKeyVisible ? "text" : "password"}
          autoComplete="new-password"
          data-lpignore="true"
          data-1p-ignore
          data-op-ignore
          data-bwignore="true"
          placeholder={hidePlaceholders ? "Enter your xAI API key" : "xai-..."}
          value={apiKey}
          onChange={(e) => onApiKeyChange(e.target.value)}
          className={className}
          disabled={disabled}
          required
          endAdornment={
            <Tooltip
              content={isApiKeyVisible ? "Hide API key" : "Show API key"}
            >
              <button
                type="button"
                onClick={() => setIsApiKeyVisible(!isApiKeyVisible)}
                aria-label={isApiKeyVisible ? "Hide API key" : "Show API key"}
              >
                {isApiKeyVisible ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </Tooltip>
          }
        />
      </div>
      <RealtimeInstructionsField
        value={instructions}
        placeholder={
          hidePlaceholders
            ? "Describe how your agent should respond"
            : undefined
        }
        onChange={onInstructionsChange}
        className={className}
        disabled={disabled}
      />
      {!hideAdvancedSettings && (
        <RealtimeAdvancedSettingsCard>
          <GrokRealtimeAdvancedFields
            timeoutSeconds={timeoutSeconds}
            onTimeoutSecondsChange={onTimeoutSecondsChange}
            disabled={disabled}
            className={className}
          />
        </RealtimeAdvancedSettingsCard>
      )}
    </div>
  )
}

interface OpenAIRealtimeFieldsProps extends BaseFormFieldProps {
  apiKey: string
  apiKeySource: OpenAIRealtimeApiKeySource
  instructions?: string
  model?: string
  voice?: string
  temperature?: number
  timeoutSeconds?: number
  webSearchEnabled?: boolean
  onApiKeyChange: (value: string) => void
  onApiKeySourceChange: (value: OpenAIRealtimeApiKeySource) => void
  onInstructionsChange: (value: string) => void
  onModelChange: (value: string, disableWebSearch?: boolean) => void
  onVoiceChange?: (value: string) => void
  onTemperatureChange: (value: number | undefined) => void
  onTimeoutSecondsChange: (value: number | undefined) => void
  onWebSearchEnabledChange?: (value: boolean) => void
  showGptLiveAlpha?: boolean
}

export const OpenAIRealtimeFields: React.FC<OpenAIRealtimeFieldsProps> = ({
  apiKey,
  apiKeySource,
  instructions,
  model,
  voice,
  temperature,
  timeoutSeconds,
  webSearchEnabled,
  onApiKeyChange,
  onApiKeySourceChange,
  onInstructionsChange,
  onModelChange,
  onVoiceChange,
  onTemperatureChange,
  onTimeoutSecondsChange,
  onWebSearchEnabledChange,
  showGptLiveAlpha = false,
  disabled,
  className = "",
  hideAdvancedSettings = false,
  hidePlaceholders = false,
}) => {
  const fieldIdBase = useId()
  const timeoutError = getTimeoutSecondsError(timeoutSeconds)
  const modelOptions = showGptLiveAlpha
    ? [...OPENAI_REALTIME_MODELS, ...OPENAI_GPT_LIVE_ALPHA_MODELS]
    : OPENAI_REALTIME_MODELS
  const isGptLiveAlphaSelected = OPENAI_GPT_LIVE_ALPHA_MODELS.some(
    (modelOption) => modelOption.value === model,
  )
  const isGptLiveSelected = model === "gpt-live-1"
  // Marin is available for both public Live and private alpha sessions.
  const voiceOptions = OPENAI_REALTIME_VOICES.filter(
    (voiceOption) =>
      isGptLiveSelected ||
      isGptLiveAlphaSelected ||
      !OPENAI_GPT_LIVE_ALPHA_ONLY_VOICE_VALUES.has(voiceOption.value),
  )

  return (
    <div className="space-y-4">
      <div className={onVoiceChange ? "grid grid-cols-2 gap-4" : ""}>
        <div className="space-y-2">
          <Label htmlFor={`${fieldIdBase}-model`}>Model</Label>
          <Select
            value={model || OPENAI_REALTIME_DEFAULT_MODEL}
            onValueChange={(value) => {
              if (value) {
                const isNewModelLiveAlpha = OPENAI_GPT_LIVE_ALPHA_MODELS.some(
                  (modelOption) => modelOption.value === value,
                )
                const disableWebSearch =
                  isNewModelLiveAlpha && webSearchEnabled === true
                onModelChange(value, disableWebSearch)
              }
            }}
            disabled={disabled}
          >
            <SelectTrigger
              id={`${fieldIdBase}-model`}
              aria-label="Select OpenAI Realtime Model"
            >
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {modelOptions.map((modelOption) => (
                <SelectItem key={modelOption.value} value={modelOption.value}>
                  {modelOption.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {onVoiceChange && (
          <div className="space-y-2">
            <Label
              id={`${fieldIdBase}-voice-label`}
              htmlFor={`${fieldIdBase}-voice`}
            >
              Agent Voice
            </Label>
            <Select
              value={voice || OPENAI_REALTIME_DEFAULT_VOICE}
              onValueChange={(value) => {
                if (value) {
                  onVoiceChange(value)
                }
              }}
              disabled={disabled}
            >
              <SelectTrigger
                id={`${fieldIdBase}-voice`}
                aria-labelledby={`${fieldIdBase}-voice-label`}
              >
                <SelectValue placeholder="Select a voice" />
              </SelectTrigger>
              <SelectContent>
                {voiceOptions.map((voiceOption) => (
                  <SelectItem
                    key={voiceOption.value}
                    value={voiceOption.value}
                    description={voiceOption.description}
                  >
                    {voiceOption.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      {!hideAdvancedSettings && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="mb-1 flex items-center justify-between">
              <FieldLabel tooltip="Controls how varied the model's responses are. Defaults to 0.8.">
                Temperature
              </FieldLabel>
              <span className="text-sm font-medium text-coval-text">
                {temperature !== undefined ? temperature.toFixed(1) : "0.8"}
              </span>
            </div>
            <Slider
              aria-label="Temperature"
              step={0.1}
              min={0}
              max={2}
              value={[temperature !== undefined ? temperature : 0.8]}
              onValueChange={(values) => onTemperatureChange(values[0])}
              className={className}
              disabled={disabled}
            />
          </div>
          <div className="space-y-2">
            <FieldLabel tooltip="Maximum simulation duration in seconds. Defaults to 900.">
              Timeout
            </FieldLabel>
            <Input
              type="number"
              placeholder="900"
              min={1}
              max={1800}
              value={timeoutSeconds?.toString() || ""}
              onChange={(event) =>
                onTimeoutSecondsChange(
                  parseTimeoutSecondsInput(event.target.value),
                )
              }
              className={className}
              disabled={disabled}
              error={!!timeoutError}
            />
            {timeoutError && (
              <p className="text-xs text-coval-danger-text">{timeoutError}</p>
            )}
          </div>
        </div>
      )}
      <RealtimeInstructionsField
        value={instructions}
        placeholder={
          hidePlaceholders
            ? "Describe how your agent should respond"
            : undefined
        }
        onChange={onInstructionsChange}
        className={className}
        disabled={disabled}
      />
      {!hideAdvancedSettings && (
        <RealtimeAdvancedSettingsCard>
          <OpenAIRealtimeAdvancedFields
            showTuning={false}
            apiKey={apiKey}
            apiKeySource={apiKeySource}
            model={model}
            webSearchEnabled={webSearchEnabled}
            temperature={temperature}
            timeoutSeconds={timeoutSeconds}
            onApiKeyChange={onApiKeyChange}
            onApiKeySourceChange={onApiKeySourceChange}
            onWebSearchEnabledChange={onWebSearchEnabledChange}
            onTemperatureChange={onTemperatureChange}
            onTimeoutSecondsChange={onTimeoutSecondsChange}
            disabled={disabled}
            className={className}
          />
        </RealtimeAdvancedSettingsCard>
      )}
    </div>
  )
}

export { DeepgramRealtimeFields } from "./DeepgramRealtimeFormFields"
